<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-full"
  >
    <template v-slot:header>
      <div>
        <v-card-title class="screen-title"> ポリシー編集 </v-card-title>
        <v-card-subtitle class="screen-sub-title">
          NAT(IP Forwarding)
          {{ $filter.enumTo(zone.srcZoneType, "zoneType") }}/{{
            zone.srcZoneName
          }}
          → {{ $filter.enumTo(zone.dstZoneType, "zoneType") }}/{{
            zone.dstZoneName
          }}
        </v-card-subtitle>
      </div>
      <button
        type="button"
        aria-label="Close"
        class="close"
        @click="closeModal"
      >
        ×
      </button>
    </template>
    <template v-if="isLoaded" v-slot:body>
      <v-card-text class="pa-0">
        <div class="modal-body">
          <v-container fluid>
            <v-row
              no-gutters
              justify="space-between"
              class="my-2"
              align="center"
            >
              <span>{{ table.items.length }}件を表示</span>
              <v-btn color="primary" @click="addPolicy" :disabled="limitPolicy"
                >ルール追加
              </v-btn>
            </v-row>
            <app-draggable-table
              density="compact"
              :fields="table.fields"
              :items="table.items"
              primary-key="policyId"
              :hover="true"
              @click:row="editPolicy"
              :td-class="tdClass"
              :tr-class="policyRowClass"
              sticky-header="500px"
            >
              <!-- 送信元 -->
              <template #cell(srcAddressList)="{ value }">
                <policy-src-address :src-address="{ srcAddressList: value }" />
              </template>

              <!-- IPマッピング -->
              <template #cell(ipMapping)="{ value }">
                {{ value.ipMappingName }}
              </template>

              <!-- 有効/有無 -->
              <template #cell(isPolicyStatus)="{ value }">
                {{ $filter.enumTo(value, "enable") }}
              </template>

              <!-- 備考 -->
              <template #cell(description)="{ value }">
                {{ $filter.emptyTo(value, "-") }}
              </template>

              <!-- 削除 -->
              <template #cell(delete)="{ item }">
                <v-btn
                  color="primary"
                  density="comfortable"
                  @click.stop="deletePolicy(item)"
                  >削除
                </v-btn>
              </template>
            </app-draggable-table>
          </v-container>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn variant="flat" color="primary" @click="submit">保存</v-btn>
            <v-btn variant="outlined" color="primary" @click="closeModal"
              >キャンセル
            </v-btn>
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { GetPolicyType1 } from "@/apis/NwPolicyApi";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import PolicySrcAddress from "@/components/policy/PolicySrcAddress.vue";
import type { ZoneRelationItem } from "@/apis/ZoneRelationApi";
import type { Type1NatPolicyList } from "@/modals/applianceContractSetting/type1/policy/InternetType1PolicyUtmList.vue";
import type { Type1Service } from "@/apis/ContractChangeType1Api";
import type { UpperLimitEntity } from "@/apis/UpperLimitApi";
import AppDraggableTable from "@/components/AppDraggableTable.vue";
import type { AppDraggableTableHeader } from "@/components/AppDraggableTable.vue";
import { VueFinalModal } from "vue-final-modal";
import InternetType1PolicyRuleDestNatEdit from "@/modals/applianceContractSetting/type1/policy/ipForwarding/InternetType1PolicyRuleDestNatEdit.vue";

export type Type1DestinationNatPolicy = Type1NatPolicyList["policyList"][0];

export default defineComponent({
  name: "InternetType1PolicyRuleDestNatViewRD",
  components: { AppDraggableTable, PolicySrcAddress },
  props: {
    /** ゾーン */
    zone: {
      type: Object as PropType<ZoneRelationItem>,
      required: true,
    },
    /** ポリシーリスト */
    policyList: {
      type: Array as PropType<Type1DestinationNatPolicy[]>,
      required: true,
    },
    /** type1ポリシー全体 */
    type1Policy: {
      type: Object as PropType<GetPolicyType1>,
      required: true,
    },
    /** サービスメニュー(インターネットType1) */
    type1Service: {
      type: Object as PropType<Type1Service>,
      required: true,
    },
  },
  data() {
    return {
      table: {
        fields: [
          { key: "_handle", title: "↑↓", width: "50px", align: "center" },
          { key: "policyId", title: "ID", width: "80px" },
          { key: "srcAddressList", title: "送信元アドレス", width: "150px" },
          { key: "ipMapping", title: "IPマッピング", width: "180px" },
          { key: "isPolicyStatus", title: "有効/無効", width: "70px" },
          { key: "description", title: "備考" },
          { key: "delete", title: "", width: "50px" },
        ] as AppDraggableTableHeader<Type1DestinationNatPolicy>[],
        items: cloneDeep(this.policyList),
      },
      natPolicyLimit: [] as UpperLimitEntity[],
      isLoaded: false,
      // 変更検知用の初期データ
      initialPolicyList: [] as Type1DestinationNatPolicy[],
    };
  },
  computed: {
    /** 変更があるかどうかを判定 */
    hasChanges(): boolean {
      return !isEqual(this.table.items, this.initialPolicyList);
    },
    /** true: 作成できるポリシー数の上限, false: 上限ではない */
    limitPolicy(): boolean {
      // NAT側のカスタムポリシー数
      const totalPolicy =
        (this.latestType1Policy.policyIPMasqueradeType1List
          ?.flatMap((e) => e.policyList)
          .filter((e) => e.policyCategoryInfo === "CUSTOM_POLICY").length ??
          0) +
        (this.latestType1Policy.policyNatType1List?.flatMap((e) => e.policyList)
          .length ?? 0);
      return (
        totalPolicy >=
        this.natPolicyLimit!.filter(
          (e) => e.upperLimitItemName === "ポリシー数(NAT)"
        )[0]!.upperLimit
      );
    },
    /** 最新の全体ポリシー（全体ポリシー + 編集中のポリシー ）*/
    latestType1Policy(): GetPolicyType1 {
      return {
        ...this.type1Policy,
        policyNatType1List:
          this.type1Policy.policyNatType1List?.map((e) => {
            return e.zoneRelationId === this.zone.zoneRelationId
              ? { ...e, policyList: this.table.items }
              : e;
          }) ?? [],
      };
    },
  },
  async mounted() {
    // 初期データを保存
    this.initialPolicyList = cloneDeep(this.policyList);
    /** NATの設定上限値を取得 */
    this.natPolicyLimit = (
      await this.$api.upperLimit.getUpperLimitLine({
        upperLimitLineManageUnit: "APPLIANCE",
        applianceType: "NAT",
        enumber: this.type1Service.enumberMainAct,
      })
    ).internetLine!.upperLimitList!;
    this.isLoaded = true;
  },
  methods: {
    tdClass(field: AppDraggableTableHeader<unknown>): string[] | undefined {
      if (field.key === "description") {
        return ["text-pre-wrap"];
      }
      return undefined;
    },
    /** ポリシーテーブルのtrクラス. */
    policyRowClass(): string[] | undefined {
      return ["draggable", "clickable"];
    },
    /** ポリシー追加 */
    async addPolicy() {
      const addPolicy: Type1DestinationNatPolicy = await this.$modal.show(
        InternetType1PolicyRuleDestNatEdit,
        {
          zone: this.zone,
          type1Policy: this.latestType1Policy,
        }
      );
      this.table.items.splice(this.table.items.length, 0, addPolicy);
    },

    /** ポリシー編集 */
    async editPolicy(policy: Type1DestinationNatPolicy) {
      const editPolicy: Type1DestinationNatPolicy = await this.$modal.show(
        InternetType1PolicyRuleDestNatEdit,
        {
          zone: this.zone,
          policy,
          type1Policy: this.latestType1Policy,
        }
      );
      const index = this.table.items.indexOf(policy);
      this.table.items[index] = editPolicy;
    },

    /** ポリシー削除 */
    async deletePolicy(item: Type1DestinationNatPolicy) {
      const index = this.table.items.indexOf(item);
      this.table.items.splice(index, 1);
    },

    /** ポリシー編集完了 */
    async submit() {
      (
        this.$refs.modal as typeof VueFinalModal & { ok: (e: unknown) => void }
      ).ok(this.table.items);
    },
    /** ×ボタン、キャンセルボタンクリック時の処理 */
    async closeModal() {
      // 変更がない場合はそのまま閉じる
      if (!this.hasChanges) {
        (
          this.$refs.modal as typeof VueFinalModal & { cancel: () => void }
        ).cancel();
        return;
      }

      try {
        await this.$confirm("変更を破棄してキャンセルします、よろしいですか？");
        (
          this.$refs.modal as typeof VueFinalModal & { cancel: () => void }
        ).cancel();
      } catch (error) {
        // キャンセルが押された場合：何もしない
        return;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
:deep(th) {
  font-size: 90%;
}
</style>
